/**
 * お問合せ（スタッフ紹介経由）
 *
 * contact-inquiry-flow.js をベースに複製したうえで、次のみ変更・追加しています。
 * - 入力: フォームの取得（data-contact-inquiry-staff-flow）、保存時に ciq_staff_assign / _meta
 * - 確認: contact-inquiry-flow.js の initConfirmPage のあとに実行する追記処理のみ
 *   （戻り先リンク・担当者行の表示。flow 本体は先方取り込み済みでも触らないため分離）
 */
import { initContactInquiryFormValidation, validateContactInquiryForm } from './contact-inquiry-form-validation.js';

const STORAGE_KEY = 'takamatsu_contact_inquiry_v1';
const CONFIRM_PATH = '/contact_inquiry_staff_confirm.html';

const DEFAULT_STAFF_RETURN_PATH = '/contact_inquiry_staff.html';
const DEFAULT_GENERAL_RETURN_PATH = '/contact_inquiry.html';

function getStaffFormRoot() {
	return document.querySelector('form[data-contact-inquiry-staff-flow]#contact-inquiry-form');
}

function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	return {
		ciq_name_family: pick('ciq_name_family').trim(),
		ciq_name_given: pick('ciq_name_given').trim(),
		ciq_tel: pick('ciq_tel').trim(),
		ciq_email: pick('ciq_email').trim(),
		ciq_zip: pick('ciq_zip').trim(),
		ciq_pref: pick('ciq_pref').trim(),
		ciq_city: pick('ciq_city').trim(),
		ciq_street: pick('ciq_street').trim(),
		ciq_building: pick('ciq_building').trim(),
		ciq_message: pick('ciq_message'),
	};
}

function collectFormPayload(form) {
	const fd = new FormData(form);
	const prefEl = form.querySelector('#ciq-pref');
	const prefLabel =
		prefEl && prefEl.selectedIndex >= 0 ? prefEl.options[prefEl.selectedIndex].text.trim() : '';
	const family = (fd.get('ciq_name_family') || '').toString().trim();
	const given = (fd.get('ciq_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';

	const zipRaw = (fd.get('ciq_zip') || '').toString().trim();
	const postalMark = '〒';
	const zipDisplay = zipRaw
		? `${postalMark} ${zipRaw.replace(new RegExp(`^${postalMark}\\s*`), '')}`
		: '';

	const pref = (fd.get('ciq_pref') || '').toString().trim();
	const city = (fd.get('ciq_city') || '').toString().trim();
	const street = (fd.get('ciq_street') || '').toString().trim();
	const building = (fd.get('ciq_building') || '').toString().trim();
	const prefDisplay = prefLabel && prefLabel !== '都道府県' ? prefLabel : pref || '';

	const line2 = [prefDisplay, city, street].filter(Boolean).join(' ');
	const lines = [zipDisplay, line2, building].filter((line) => line && line.length > 0);
	const addressBlock = lines.length ? lines.join('\n') : '—';

	const message = (fd.get('ciq_message') || '').toString().trim();

	return {
		ciq_name_full: nameFull,
		ciq_tel: (fd.get('ciq_tel') || '').toString().trim() || '—',
		ciq_email: (fd.get('ciq_email') || '').toString().trim() || '—',
		ciq_address_block: addressBlock,
		ciq_message: message || '—',
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	for (const key of Object.keys(raw)) {
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function initStaffFormPage() {
	const form = getStaffFormRoot();
	if (!form) return;

	initContactInquiryFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	const staffAssign = (form.getAttribute('data-ciq-staff-assign') || '').trim();
	const returnPath =
		(form.getAttribute('data-ciq-return-form-path') || '').trim() || DEFAULT_STAFF_RETURN_PATH;

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateContactInquiryForm(form)) {
			return;
		}
		const confirmPayload = collectFormPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({
			...confirmPayload,
			ciq_staff_assign: staffAssign || '—',
			_restore: raw,
			_meta: { returnFormPath: returnPath, addressOptional: true },
		});
		window.location.href = CONFIRM_PATH;
	});
}

/**
 * 確認ページ: flow の表示ロジックのあとで、スタッフ経由用の UI のみ調整する（送信ボタン等は触らない）
 */
function initStaffConfirmAugment() {
	const page = document.querySelector('[data-contact-inquiry-page="confirm"]');
	if (!page) return;

	const draft = loadDraft();
	const returnUrl = draft?._meta?.returnFormPath ?? DEFAULT_GENERAL_RETURN_PATH;
	const back = document.getElementById('contact-inquiry-confirm-back');
	const emptyLink = document.getElementById('contact-inquiry-confirm-empty-link');
	if (back) back.href = returnUrl;
	if (emptyLink) emptyLink.href = returnUrl;

	const staffRowVisible =
		Boolean(draft) &&
		typeof draft.ciq_staff_assign === 'string' &&
		draft.ciq_staff_assign.length > 0 &&
		draft.ciq_staff_assign !== '—';
	const staffRow = document.getElementById('contact-inquiry-confirm-staff-row');
	if (staffRow) {
		staffRow.hidden = !staffRowVisible;
	}
}

export function initContactInquiryStaffFlow() {
	initStaffFormPage();
	initStaffConfirmAugment();
}
